import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useAppSelector } from 'store/hooks'
import { UseMutationResult } from '@tanstack/react-query'
import { produce } from 'immer'
import { v4 as uuid } from 'uuid'
import { IDropdownOption } from 'models/shared.models'
import Catalogue from 'models/catalogues'
import Client from 'models/client'
import {
  Concept,
  IInvoiceFormSerie,
  InvoiceForm,
  TaxCatalogue,
  TInvoiceDraftMenuOptions,
} from 'models/invoices.models'
import Product from 'models/product'
import RazonSocial from 'models/razonSocial'
import { useThunkDispatch } from 'models/thunk'
import { INVOICE_DRAFT_MENU_OPTIONS } from 'lib/enums/invoices.enums'
import { selectErrorDialog } from 'lib/helpers/errors.helpers'
import {
  selectActiveWorkspace,
  selectCatalogue,
  selectClientsList,
  selectCreateInvoice,
  selectHideSaveProgress,
  selectProductsList,
  selectRazonesSocialesList,
} from 'lib/helpers/selectors'
import { getYearList } from 'lib/helpers/utilities'
import { invoiceDraftMenuOptionsLabelMap } from 'lib/utils/Invoices/invoices.utils'
import useDialog from 'hooks/Shared/useDialog'
import useSaveInvoiceCreationProgress from 'hooks/Invoices/CFDIs/useSaveInvoiceCreationProgress'
import { getCategoriesList } from 'actions/categories'
import { getClientsList } from 'actions/clients'
import { storeFormValue } from 'actions/forms'
import {
  createConcept,
  createIncomeInvoice,
  getSerieFolioHistorial,
  getTaxCatalogue,
  setHideSaveProgress,
} from 'actions/invoices'
import { setLocalLoader } from 'actions/loader'
import { getProductsList } from 'actions/products'
import { getRazonesSocialesList } from 'actions/razonesSociales'
import { IApiError } from 'services/Shared/shared.models'
import { IAddIncomeCFDIContainerProps } from './AddIncomeCFDIContainer'
import InfoIcon from 'assets/icons/InfoIcon'
import { Checkbox } from '@mui/material'

export interface IAddIncomeCFDIContainerState {
  lastFolio: string
  loading: boolean
  serieCatalog: Record<string, any>[]
  taxes: TaxCatalogue
  valid: boolean
}

export interface IUseAddIncomeCFDIContainer
  extends IAddIncomeCFDIContainerState {
  clients: Client[]
  emisores: RazonSocial[]
  form: InvoiceForm
  formasPago: Catalogue
  metodosPago: Catalogue
  monedas: Catalogue
  months: Catalogue
  periods: Catalogue
  products: Product[]
  usosCfdi: Catalogue
  years: Catalogue
  saveInvoiceCreationProgressMutation: UseMutationResult<
    void,
    IApiError,
    InvoiceForm
  >
  draftMenuOptionActions: Readonly<
    Record<TInvoiceDraftMenuOptions, (form: InvoiceForm) => void>
  >
  invoiceDraftMenuOptions: readonly IDropdownOption[]
  updateState: (
    draftUpdater: (draft: IAddIncomeCFDIContainerState) => void
  ) => void
  saveValueToStore: (name: string, value: any) => void
  filterClients: (clients: Client[], value: string) => Client[]
  setEmisor: (value: RazonSocial) => void
  setSerie: (actualSerie: any) => void
  handleNewSerie: (serie: any) => void
  handleCleanSerie: (value: any) => void
  addConcept: (p?: Product) => void
  onAdd: (formValues: any, option?: TPDFOption) => Promise<void>
}

export type TPDFOption = 'view-PDF' | 'download-PDF'

const useAddIncomeCFDIContainer = ({
  updatePDFDocument,
  togglePDFViewer,
  downloadPDFHandler,
  cleanUpAndLeave,
}: IAddIncomeCFDIContainerProps): IUseAddIncomeCFDIContainer => {
  const dispatch = useDispatch()
  const thunkDispatch = useThunkDispatch()

  const dialog = useDialog()

  const clients = useAppSelector(selectClientsList)
  const emisores = useAppSelector(selectRazonesSocialesList)
  const form = useAppSelector(selectCreateInvoice)
  const hideSaveProgress = useAppSelector(selectHideSaveProgress)
  const formasPago = useAppSelector(selectCatalogue('formasPago'))
  const metodosPago = useAppSelector(selectCatalogue('metodosPago'))
  const monedas = useAppSelector(selectCatalogue('monedas'))
  const months = useAppSelector(selectCatalogue('meses'))
  const periods = useAppSelector(selectCatalogue('periodicidad'))
  const products = useAppSelector(selectProductsList)
  const tiposComprobantes = useAppSelector(selectCatalogue('tiposComprobantes'))
  const usosCfdi = useAppSelector(selectCatalogue('usosCfdi4'))
  const activeWorkspace = useAppSelector(selectActiveWorkspace)

  const years = useMemo(() => getYearList(new Date()), [])

  const [addIncomeCFDIContainerState, setAddIncomeCFDIContainerState] =
    useState<IAddIncomeCFDIContainerState>({
      lastFolio: '',
      loading: false,
      serieCatalog: [],
      taxes: [],
      valid: false,
    })

  const { serieCatalog, valid } = addIncomeCFDIContainerState

  const updateState = useCallback(
    (draftUpdater: (draft: IAddIncomeCFDIContainerState) => void) => {
      setAddIncomeCFDIContainerState(produce(draftUpdater))
    },
    []
  )

  const saveValueToStore = useCallback(
    (name: string, value: any) => {
      dispatch(storeFormValue('createInvoice', name, value))
    },
    [dispatch]
  )

  const getFolios = useCallback(
    async emisor => {
      try {
        const req = await thunkDispatch(
          getSerieFolioHistorial(activeWorkspace, emisor.idRfc)
        )
        const serieCatalog = req.map((element: any) => {
          return {
            value: element.serie,
            label: element.serie,
            folio: element.folio,
          }
        })
        updateState(draft => {
          draft.serieCatalog = serieCatalog
        })
      } catch (err) {
        console.log(err)
      }
    },
    [activeWorkspace, thunkDispatch, updateState]
  )

  const getInformation = useCallback(async () => {
    dispatch(setLocalLoader(true, 'Cargando catálogos personalizados'))
    try {
      const req = await Promise.all([
        thunkDispatch(getTaxCatalogue()),
        thunkDispatch(getClientsList(activeWorkspace)),
        thunkDispatch(getRazonesSocialesList(activeWorkspace)),
        thunkDispatch(getProductsList(activeWorkspace)),
        thunkDispatch(getCategoriesList(activeWorkspace)),
      ])
      updateState(draft => {
        draft.taxes = req[0]
      })
      dispatch(setLocalLoader(false))
      if (form.emisor?.idRfc) {
        getFolios(form.emisor)
      }
    } catch (err) {
      dialog.danger({
        title: 'generic.error.title',
        subtitle:
          'No se pudo obtener la información de los catálogos. Intenta más tarde.',
        primaryButtonText: 'Reintentar',
        secondaryButtonText: 'Cancelar',
        onPrimaryClick: () => {
          dispatch(setLocalLoader(false))
          getInformation()
        },
        onSecondaryClick: () => {
          dispatch(setLocalLoader(false))
          cleanUpAndLeave()
        },
      })
    }
  }, [
    activeWorkspace,
    dialog,
    form.emisor,
    dispatch,
    thunkDispatch,
    updateState,
    cleanUpAndLeave,
    getFolios,
  ])

  const filterClients = (clients: Client[], value: string) => {
    if (!value) return clients
    const search = value.toLowerCase()
    const filtered = clients.filter(c => {
      const likeAlias = c.alias?.toLowerCase().includes(search)
      const likeName = c.name?.toLowerCase().includes(search)
      const likeRFC = c.rfc?.toLowerCase().includes(search)
      return likeAlias || likeName || likeRFC
    })
    return filtered
  }

  const suggestFolio = useCallback((folio?: string) => {
    if (!folio) return null
    const onlyNumber = /\D/
    if (!onlyNumber.test(folio)) {
      const length = folio.toString().length
      const nextNumber = parseFloat(folio) + 1
      const suggest = nextNumber.toString().padStart(length, '0')
      return suggest
    } else {
      return null
    }
  }, [])

  const setEmisor = useCallback(
    (value: RazonSocial) => {
      saveValueToStore('emisor', value)
      if (value !== null) {
        getFolios(value)
      }
    },
    [getFolios, saveValueToStore]
  )

  const setSerie = useCallback(
    (actualSerie: IInvoiceFormSerie) => {
      const actual: any = serieCatalog.find(
        (serie: any) => serie.value === actualSerie.value
      )
      if (actualSerie !== null && actual !== null) {
        saveValueToStore('serie', actualSerie)
        const suggest = suggestFolio(actualSerie.folio)
        if (suggest !== null) {
          saveValueToStore('folio', suggest)
          actualSerie.folio !== undefined &&
            updateState(draft => {
              draft.lastFolio = actualSerie.folio as string
            })
        } else {
          saveValueToStore('folio', '')
          updateState(draft => {
            draft.lastFolio = actualSerie.folio as string
          })
        }
      }
    },
    [serieCatalog, updateState, saveValueToStore, suggestFolio]
  )

  const handleNewSerie = useCallback(
    (serie: any) => {
      if (serie !== null) {
        const newSerie = { value: serie.value, label: serie.value, folio: '' }
        saveValueToStore('serie', newSerie)
        updateState(draft => {
          draft.serieCatalog.push(newSerie)
          draft.lastFolio = ''
        })
      } else {
        saveValueToStore('serie', null)
        updateState(draft => {
          draft.lastFolio = ''
        })
      }
    },
    [saveValueToStore, updateState]
  )

  const handleCleanSerie = useCallback(
    (value: any) => {
      if (!value) {
        saveValueToStore('serie', null)
        saveValueToStore('folio', '')
        updateState(draft => {
          draft.lastFolio = ''
        })
      }
    },
    [updateState, saveValueToStore]
  )

  const addConcept = (p?: Product) => {
    const { claveSat, claveUnitSat, description, objImp, price } = p ?? {}

    const concept: Concept = {
      cantidad: '1',
      claveSat,
      claveUnitSat,
      description: description ?? '',
      descuento: '',
      importacion: '',
      impuestos: [],
      objImp,
      predial: '',
      price: price ? `${price}` : '',
      satKey: claveSat,
      uuid: uuid(),
    }

    dispatch(createConcept(concept))
  }

  const handlePDFResponse = (
    data: { base64Pre: string },
    option: TPDFOption
  ) => {
    const { base64Pre } = data

    updatePDFDocument(base64Pre)

    option === 'view-PDF' && togglePDFViewer()
    option === 'download-PDF' && downloadPDFHandler(base64Pre)

    updateState(draft => {
      draft.loading = false
    })
  }

  const saveInvoiceCreationProgressMutation = useSaveInvoiceCreationProgress(
    activeWorkspace,
    cleanUpAndLeave
  )

  const onAdd = async (formValues: any, option?: TPDFOption) => {
    updateState(draft => {
      draft.loading = true
    })

    try {
      const response = await thunkDispatch(
        createIncomeInvoice(activeWorkspace, form, !!option)
      )

      option ? handlePDFResponse(response, option) : cleanUpAndLeave(true)
    } catch (err: any) {
      const op = selectErrorDialog(err)

      updateState(draft => {
        draft.loading = false
      })

      if (op?.variant) {
        await dialog[op.variant](op)
        return
      }

      dialog.danger({
        title: 'generic.error.title',
        subtitle: 'No se pudo crear el CFDI. Intenta nuevamente.',
      })
    }
  }

  const draftMenuOptionActions: Readonly<
    Record<TInvoiceDraftMenuOptions, (form: InvoiceForm) => void>
  > = {
    [INVOICE_DRAFT_MENU_OPTIONS.GUARDAR]: form => {
      if (hideSaveProgress) {
        saveInvoiceCreationProgressMutation.mutate(form)
      } else {
        dialog.primary({
          className: 'save-progress-confirmation',
          ariaLabel: 'modal de confirmación de guardado de progreso',
          title: 'Guardar progreso',
          subtitle:
            'Encontrarás el borrador de este CFDI en tu lista de prefacturas.',
          modalContent: (
            <div className="confirmation-row">
              <Checkbox
                onChange={(_, checked) => {
                  dispatch(setHideSaveProgress(checked))
                }}
              />
              <span>No volver a mostrar este mensaje</span>
            </div>
          ),
          primaryButtonText: 'Guardar progreso',
          secondaryButtonText: 'Cancelar',
          loadingPrimaryButton: saveInvoiceCreationProgressMutation.isLoading,
          icon: InfoIcon,
          onClose: () => null,
          onPrimaryClick: () =>
            saveInvoiceCreationProgressMutation.mutate(form),
        })
      }
    },
    [INVOICE_DRAFT_MENU_OPTIONS.VISTA_PREVIA]: form => {
      onAdd(form, 'view-PDF')
    },
    [INVOICE_DRAFT_MENU_OPTIONS.DESCARGAR_PDF]: form =>
      onAdd(form, 'download-PDF'),
  } as const

  const invoiceDraftMenuOptions: Readonly<IDropdownOption[]> = [
    {
      id: INVOICE_DRAFT_MENU_OPTIONS.GUARDAR,
      value: INVOICE_DRAFT_MENU_OPTIONS.GUARDAR,
      label:
        invoiceDraftMenuOptionsLabelMap[INVOICE_DRAFT_MENU_OPTIONS.GUARDAR],
    },
    {
      id: INVOICE_DRAFT_MENU_OPTIONS.VISTA_PREVIA,
      value: INVOICE_DRAFT_MENU_OPTIONS.VISTA_PREVIA,
      label:
        invoiceDraftMenuOptionsLabelMap[
          INVOICE_DRAFT_MENU_OPTIONS.VISTA_PREVIA
        ],
      disabled: !valid,
    },
    {
      id: INVOICE_DRAFT_MENU_OPTIONS.DESCARGAR_PDF,
      value: INVOICE_DRAFT_MENU_OPTIONS.DESCARGAR_PDF,
      label:
        invoiceDraftMenuOptionsLabelMap[
          INVOICE_DRAFT_MENU_OPTIONS.DESCARGAR_PDF
        ],
      disabled: !valid,
    },
  ] as const

  useEffect(() => {
    getInformation()
  }, [getInformation])

  useEffect(() => {
    if (!form.tipo) {
      const income = tiposComprobantes.filter(m => m.value === 'I')[0]
      saveValueToStore('tipo', income)
    }

    if (!form.moneda) {
      const mxn = monedas.filter(m => m.value === 'MXN')[0]
      saveValueToStore('moneda', mxn)
    }

    /* Exception to the exhaustive deps rule: for better ux, the user is able to set the currency and periodicity year as null, even if by mistake,
    as not to feel that the system doesn't accept his/her inputs, but the default values are set on mount,
    and they will be required (when applicable) to validate the invoice. */

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monedas, tiposComprobantes, form.tipo, saveValueToStore])

  useEffect(
    () => () => {
      updateState(draft => {
        draft.loading = false
      })
    },
    [updateState]
  )

  return {
    ...addIncomeCFDIContainerState,
    clients,
    emisores,
    form,
    formasPago,
    metodosPago,
    monedas,
    months,
    periods,
    products,
    usosCfdi,
    years,
    saveInvoiceCreationProgressMutation,
    draftMenuOptionActions,
    invoiceDraftMenuOptions,
    updateState,
    saveValueToStore,
    filterClients,
    setEmisor,
    setSerie,
    handleNewSerie,
    handleCleanSerie,
    addConcept,
    onAdd,
  }
}

export default useAddIncomeCFDIContainer
